import { useAtom } from 'jotai';
import { accessibilityDataAtom, demoModeAtom, userAtom } from '@/global-store';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarInset,
} from '@layout/Sidebar/Sidebar';
import { useSidebar } from '@layout/Sidebar/SidebarContext';
import { Link as RouterLink, Outlet, useNavigation } from 'react-router-dom';
import { MessageCircleQuestion, MessageSquareText } from 'lucide-react';
import { useFetchAccessibilityData } from '@http/queries';
import MenuList from './Sidebar/MenuList/MenuList';
import Header from '@layout/Header/Header';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import Footer from '@layout/Footer';
import LoadingSpinner from '@components/LoadingSpinner';
import { usePathname } from '@hooks/usePathname';
import { cn } from '@helpers/cn';
import OfferContactForm from '@components/OfferContactForm';
import theme from '@assets/theme';
import { Box } from '@mui/material';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';

export function MainLayout() {
  const { isMobile, openMobile, state } = useSidebar();

  const [, setAccessibilityData] = useAtom(accessibilityDataAtom);
  const [demoMode] = useAtom(demoModeAtom);
  const [user] = useAtom(userAtom);

  const { t, i18n } = useInlineTranslation();

  const path = usePathname();

  const isReportArea = path.startsWith('/report');
  const isRulesArea = path.startsWith('/rulesOverview');

  // fetch the accessibility data for the current auditEntry and update the atom if the data changes
  const { isLoading } = useFetchAccessibilityData({
    onDataChanged: (data) => setAccessibilityData(data),
  });

  const xlAndUp = useTailwindBreakpoint('xl');
  const navigation = useNavigation();

  const isPageLoading =
    navigation.state === 'loading' ||
    ((isReportArea || isRulesArea) && isLoading);

  const renderMainContent = () => {
    if (isPageLoading) {
      return <LoadingSpinner />;
    }
    return <Outlet />;
  };

  const shouldShowDemoButtons = demoMode
    ? isMobile
      ? !openMobile
      : state === 'collapsed'
    : false;

  const shouldShowForInternalFeedback = user?.primaryOrganisation === 1;

  return (
    <div
      className='eye:flex eye:flex-col eye:min-h-svh eye:w-full'
      style={{
        ['--app-header-height']: `${xlAndUp ? 75 : 52}px`,
      }}
    >
      <Header />
      <div aria-hidden style={{ height: 'var(--app-header-height)' }} />
      <div
        className='eye:bg-grey-10 eye:flex eye:w-full eye:flex-1'
        style={{ paddingRight: 'var(--removed-body-scroll-bar-size, 0px)' }}
      >
        <Sidebar
          className='no-print'
          collapsible='icon'
          side='left'
          variant='inset'
        >
          <SidebarContent className='eye:bg-white'>
            <MenuList />
          </SidebarContent>
          {!demoMode && (
            <SidebarFooter className='eye:gap-xs'>
              {shouldShowForInternalFeedback && (
                <Button className='eye:w-full' variant='outline' asChild>
                  <a
                    href={
                      i18n.resolvedLanguage === 'de'
                        ? 'https://form.asana.com/?k=UFeLGkl2Ortj6cEohzwSrA&d=1199244943732308'
                        : 'https://form.asana.com/?k=1L5CImM7ygeCcSEFICxD_Q&d=1199244943732308'
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <MessageSquareText aria-hidden />
                    {t('productFeedback')}
                  </a>
                </Button>
              )}
              <div id='support-popup'>
                <Button className='eye:w-full' variant='outline' asChild>
                  <RouterLink to='/help-center'>
                    <MessageCircleQuestion />
                    {t('helpCenter')}
                  </RouterLink>
                </Button>
              </div>
            </SidebarFooter>
          )}
        </Sidebar>

        <div
          className={cn(
            'eye:min-w-0 eye:max-w-full',
            'eye:px-16 eye:pt-32 eye:sm:px-6 eye:sm:py-6 eye:lg:px-48 eye:lg:py-[40px]',
            'eye:flex eye:flex-col eye:flex-1 eye:flex-nowrap',
            'eye:overflow-x-clip eye:wrap-break-word',
          )}
        >
          <SidebarInset className='eye:w-full eye:max-w-[1500px] eye:mx-auto'>
            {renderMainContent()}
            <Footer />
          </SidebarInset>
        </div>
      </div>

      {shouldShowDemoButtons ? (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            zIndex: 1150,
            width: '100%',

            '& .MuiButton-root': {
              width: '100%',
              borderRadius: 0,
            },

            '@media (min-width:1190px)': {
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'auto',

              '& .MuiButton-root': {
                width: 'auto !important',
                borderRadius: '50px !important',
              },

              top: '16.5px',
              bottom: 'unset',
            },

            '@media (max-width:1189px)': {
              '& .MuiButton-root, & #offer-contact-button': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.black.main,
                width: '50%',
              },
              '& .MuiButton-root': {
                borderRight: '0.75px solid #000',
              },
              '& #offer-contact-button .MuiButton-root': {
                width: '100%',
                borderLeft: '0.75px solid #000',
                borderRight: 'none',
              },
            },

            '@media (max-width:579px)': {
              '& .MuiButton-root, & #offer-contact-button': {
                width: '100%',
              },
            },
          }}
        >
          <OfferContactForm
            buttonColor='secondary'
            fullWidthMobile
            size='small'
            showBookConsultation
          />
        </Box>
      ) : null}
    </div>
  );
}
